import { useState, useEffect } from 'react';

interface UserAvatarProps {
  email?: string | null;
  name?: string | null;
  avatarUrl?: string | null;
  /** Tailwind size + text-size classes, e.g. "h-8 w-8 text-sm" */
  className?: string;
}

async function computeGravatarUrl(email: string, pixelSize: number): Promise<string> {
  const normalized = email.trim().toLowerCase();
  const data = new TextEncoder().encode(normalized);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hash = Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  // d=404 so a missing Gravatar returns HTTP 404, triggering onError
  return `https://www.gravatar.com/avatar/${hash}?s=${pixelSize}&d=404`;
}

/**
 * Displays a user avatar. Priority:
 *  1. Explicit avatarUrl
 *  2. Gravatar (SHA-256 hash of email)
 *  3. Fallback: first letter of name or email
 */
export function UserAvatar({ email, name, avatarUrl, className = 'h-8 w-8 text-sm' }: UserAvatarProps) {
  const [gravatarUrl, setGravatarUrl] = useState<string | null>(null);
  const [imgError, setImgError] = useState(false);

  const initial = (name?.[0] || email?.[0] || '?').toUpperCase();
  const src = avatarUrl || gravatarUrl;

  useEffect(() => {
    if (avatarUrl || !email) {
      setGravatarUrl(null);
      return;
    }
    setImgError(false);
    // Request 2x the rendered size for retina displays
    computeGravatarUrl(email, 160).then(setGravatarUrl);
  }, [email, avatarUrl]);

  // Reset error state whenever the image source changes
  useEffect(() => {
    setImgError(false);
  }, [src]);

  if (src && !imgError) {
    return (
      <img
        src={src}
        alt={name || email || 'User'}
        className={`shrink-0 rounded-full object-cover ${className}`}
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-full bg-primary font-medium text-primary-foreground ${className}`}
    >
      {initial}
    </div>
  );
}
